import { ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { GlobalMessageType } from '@spartacus/core'
import { finalize, switchMap } from 'rxjs/operators'
import { AgreementStatus, AgreementStatusDisplay } from '../../../../../models/enum/agreement.enum'
import { SharedErrorType, SharedValue } from '../../../../../models/enum/shared.enum'
import { IAgreement } from '../../../../../models/interface/agreement.interface'
import { ICsv } from '../../../../../models/interface/csv.interface'
import { IHistoryList } from '../../../../../models/interface/history-list.interface'
import { AgreementService } from '../../../../../services/agreement/agreement.service'
import { PriceSupportAgreementService } from '../../../../../services/agreement/price-support/price-support-agreement.service'
import { CsvService } from '../../../../../services/shared/csv/csv.service'
import { CustomGlobalMessageService } from '../../../../../services/shared/custom-global-message/custom-global-message.service'
import { CustomModalService } from '../../../../../services/shared/modal/custom-modal.service'
import { SharedService } from '../../../../../services/shared/shared.service'
import { CustomTableColumnType } from '../../../../shared/custom-components/custom-table/modals/enum/custom-table.enum'
import {
  ICustomTableColumn,
  ICustomTableHeader
} from '../../../../shared/custom-components/custom-table/modals/interface/custom-table.interface'
import { PriceAgreementAcceptComponent } from '../price-agreement-accept-reject/price-agreement-accept/price-agreement-accept.component'
import { PriceAgreementRejectComponent } from '../price-agreement-accept-reject/price-agreement-reject/price-agreement-reject.component'

@Component({
    selector: 'app-price-agreement-history-detail',
    templateUrl: './price-agreement-history-detail.component.html',
    styleUrls: ['./price-agreement-history-detail.component.scss'],
    standalone: false
})
export class PriceAgreementHistoryDetailComponent implements OnInit {
  public agreementCode: string
  public agreement: IAgreement
  public entries: IHistoryList
  public showLoaderOnPagination: boolean
  public showInitialLoader = true
  public showError404: boolean

  public headers: ICustomTableHeader[] = [
    { title: 'priceSupportAgreement.sku' },
    { title: 'priceSupportAgreement.description' },
    { title: 'priceSupportAgreement.startDate' },
    { title: 'priceSupportAgreement.endDate' },
    { title: 'priceSupportAgreement.status' },
    { title: 'priceSupportAgreement.netInvoicePrice' },
    { title: 'priceSupportAgreement.rebates' },
    { title: 'priceSupportAgreement.dbp' },
    { title: 'priceSupportAgreement.endCustomerPrice' },
    { title: 'priceSupportAgreement.volumeCommitment' }
  ]

  public columns: ICustomTableColumn[] = [
    { key: 'product.code' },
    { key: 'product.name' },
    { key: 'startDate', type: CustomTableColumnType.DATE },
    { key: 'endDate', type: CustomTableColumnType.DATE },
    { key: 'quoteEntryStatus' },
    { key: 'netInvoiceValue.formattedValue' },
    { key: 'rebateAmount.formattedValue' },
    { key: 'dbp.formattedValue' },
    { key: 'endCustomerPrice.formattedValue' },
    { key: 'quantity' }
  ]

  constructor(
    private priceSupportAgreementService: PriceSupportAgreementService,
    private globalMessageService: CustomGlobalMessageService,
    public sharedService: SharedService,
    private csvService: CsvService,
    private changeDetectorRef: ChangeDetectorRef,
    private agreementService: AgreementService,
    private router: Router,
    private modalService: CustomModalService
  ) {}

  ngOnInit(): void {
    this.sharedService
      .getUrlParam('code')
      .pipe(
        switchMap((code) => {
          this.agreementCode = code
          return this.priceSupportAgreementService.getAgreementDetail(code)
        }),
        switchMap((detail) => {
          this.agreement = detail
          this.changeDetectorRef.detectChanges()
          return this.priceSupportAgreementService.getAgreementDetailEntries(detail?.orderCode)
        }),
        finalize(() => {
          this.showInitialLoader = false
          this.changeDetectorRef.detectChanges()
        })
      )
      .subscribe(
        (entries) => {
          this.entries = entries
        },
        (err) => {
          if (err.type === SharedErrorType.notFound404) {
            this.showError404 = true
          }
        }
      )
  }

  public downloadCSV(code: string): void {
    this.priceSupportAgreementService.getAgreementDetailAllEntries(code).subscribe(
      (entries) => {
        const csv: ICsv = {
          title: `Price Support Agreement Detail ${code}`,
          columns: [
            { title: 'priceSupportAgreement.agreementNumber', key: 'orderCode', value: this.agreement.orderCode },
            {
              title: 'priceSupportAgreement.endUserNumber',
              key: 'endCustomer.orgUnit.uid',
              value: this.agreement.endCustomer?.orgUnit?.uid
            },
            {
              title: 'priceSupportAgreement.endUserName',
              key: 'endCustomer.orgUnit.name',
              value: this.agreement.endCustomer?.orgUnit?.name
            },
            {
              title: 'priceSupportAgreement.distributorEndUserAccount',
              key: 'accountNumber',
              value: this.agreement.accountNumber
            },
            { title: 'priceSupportAgreement.division', key: 'division', value: this.agreement.division },
            { title: 'priceSupportAgreement.sku', key: 'product.code' },
            { title: 'priceSupportAgreement.skuDescription', key: 'product.name' },
            { title: 'priceSupportAgreement.startDate', key: 'startDate', type: CustomTableColumnType.DATE },
            { title: 'priceSupportAgreement.endDate', key: 'endDate', type: CustomTableColumnType.DATE },
            { title: 'priceSupportAgreement.status', key: 'quoteEntryStatus' },
            { title: 'priceSupportAgreement.netInvoicePrice', key: 'netInvoiceValue.formattedValue' },
            { title: 'priceSupportAgreement.rebates', key: 'rebateAmount.formattedValue' },
            { title: 'priceSupportAgreement.dbp', key: 'dbp.formattedValue' },
            { title: 'priceSupportAgreement.endCustomerPrice', key: 'endCustomerPrice.formattedValue' },
            { title: 'priceSupportAgreement.volumeCommitment', key: 'quantity' }
          ],
          rows: entries
        }
        this.csvService.generateCSV(csv)
      },
      (err) => {
        this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
      }
    )
  }

  public getTermsAndConditionsPDF(agreement: IAgreement): void {
    this.priceSupportAgreementService.getTermsAndConditionsPDF(agreement)
  }

  public pageChange(page: number): void {
    this.showLoaderOnPagination = true
    this.priceSupportAgreementService
      .getAgreementDetailEntries(this.agreementCode, { pageNumber: page })
      .pipe(
        finalize(() => {
          this.showLoaderOnPagination = false
          this.changeDetectorRef.detectChanges()
        })
      )
      .subscribe(
        (res) => {
          if (!res) {
            this.globalMessageService.add(SharedValue.errorTryAgain, GlobalMessageType.MSG_TYPE_ERROR)
          } else {
            this.entries = res
          }
        },
        (err) => {
          if (err.type === SharedErrorType.notFound404) {
            this.showError404 = true
          }
        }
      )
  }

  public get showWhenPending(): boolean {
    return this.agreement.state == AgreementStatus.PENDING
  }

  public acceptAgreement(agreement: IAgreement): void {
    const data = {
      agreement
    }
    this.modalService
      .showModal(PriceAgreementAcceptComponent, data)
      .then((code) => {
        if (!code) {
          return
        }
        this.agreement.state = AgreementStatus.ACCEPTED
        this.agreement.stateDisplay = AgreementStatusDisplay.ACCEPTED
        this.changeDetectorRef.detectChanges()
      })
      .catch((err) => {
        this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
      })
  }

  public rejectAgreement(agreement: IAgreement): void {
    const data = {
      agreement
    }
    this.modalService
      .showModal(PriceAgreementRejectComponent, data)
      .then((code) => {
        if (!code) {
          return
        }
        this.agreement.state = AgreementStatus.REJECTED
        this.agreement.stateDisplay = AgreementStatusDisplay.REJECTED
        this.changeDetectorRef.detectChanges()
      })
      .catch((err) => {
        this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
      })
  }

  public showDownloadCSV(): boolean {
    return this.entries?.results?.length > 0
  }
}
